// Search-state for the events index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly. Tab / date / salesperson /
// search changes reset page so a previous filter's pagination never bleeds
// into the new view.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import { EVENT_TAB_VALUES, type EventTab } from '../components/EventManagement/types';

export interface EventsSearchParams {
  tab: EventTab;
  q: string;
  page: number;
  perPage: number;
  salespersonIds: number[];
  formats: string[];
  orderStatuses: string[];
  eventStatuses: string[];
  // ISO date string (YYYY-MM-DD) when the user has picked a specific date,
  // empty string otherwise. Kept as a string in URL state so the value is
  // stable across renders; consumers convert to Date when needed.
  date: string;
}

export const EVENTS_SEARCH_DEFAULTS: EventsSearchParams = {
  tab: 'all',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  salespersonIds: [],
  formats: [],
  orderStatuses: [],
  eventStatuses: [],
  date: '',
};

const EVENTS_ROUTE = '/events' as const;

const VALID_TABS: ReadonlySet<EventTab> = new Set(EVENT_TAB_VALUES);

function toNumberArray(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',').map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

function toStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',');
  }
  return [];
}

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseEventsSearch(raw: Record<string, unknown>): EventsSearchParams {
  const tab: EventTab = VALID_TABS.has(raw.tab as EventTab)
    ? (raw.tab as EventTab)
    : EVENTS_SEARCH_DEFAULTS.tab;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const date = typeof raw.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.date) ? raw.date : '';
  return {
    tab,
    q: typeof raw.q === 'string' ? raw.q : EVENTS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : EVENTS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : EVENTS_SEARCH_DEFAULTS.perPage,
    salespersonIds: toNumberArray(raw.salespersonIds),
    formats: toStringArray(raw.formats),
    orderStatuses: toStringArray(raw.orderStatuses),
    eventStatuses: toStringArray(raw.eventStatuses),
    date,
  };
}

export function useEventsRouterSearch() {
  const search = useSearch({ from: EVENTS_ROUTE });
  const tanNavigate = useNavigate({ from: EVENTS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<EventsSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setTab: (tab: EventTab) => navigate({ tab, date: '', page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSalespersonIds: (salespersonIds: number[]) => navigate({ salespersonIds, page: 1 }),
      setFormats: (formats: string[]) => navigate({ formats, page: 1 }),
      setOrderStatuses: (orderStatuses: string[]) => navigate({ orderStatuses, page: 1 }),
      setEventStatuses: (eventStatuses: string[]) => navigate({ eventStatuses, page: 1 }),
      setDate: (date: string) => navigate({ date, page: 1 }),
    }),
    [search, navigate],
  );
}
